import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { NewConfirmModal } from "ui/NewConfirmModal"
import BlackoutFormModal from "pagesV2/pro/Settings/Blackout/modals/BlackoutFormModal"
import { useBlackouts } from "pagesV2/pro/Settings/Blackout/hooks/useBlackouts"
import BlackoutDayCard from "pagesV2/pro/Calendar/DayDetailsPanel/BlackoutDayCard"
import { BLACKOUT_SCOPES, isSameId } from "helpers/blackouts"
import styles from "./index.module.css"
import DeleteBlackoutMessage from "ui/Blackout/DeleteBlackoutMessage"

/**
 * A settings-list record in the shape `BlackoutDayCard` reads.
 *
 * The card was written for the calendar feed, which is already day-resolved — it states
 * `all_listings`, and lists `listings`/`resources` directly. A settings record instead nests those
 * under `impact` and describes its dates as `entries`, so handing one over unconverted left the
 * card reading an empty listing array and reporting "0 listings affected".
 *
 * `all_listings` follows the same rule the calendar store applies: a business blackout closes
 * everything by definition, whatever its impact happens to count.
 */
const toDayCardShape = (record, date) => {
  const entries = record.entries || []
  // The record's own span, so a closure running Sep 5-16 dates itself rather than the day the
  // panel happens to be open on. Dates are ISO, so a string compare is the range check.
  const entry = entries.find((item) => item.start_date <= date && date <= (item.end_date || item.start_date)) || entries[0]

  return {
    ...record,
    all_listings: record.all_listings ?? record.scope === BLACKOUT_SCOPES.BUSINESS,
    listings: record.listings || record.impact?.listings || [],
    resources: record.resources || record.impact?.resources || [],
    bookings_requiring_action: record.bookings_requiring_action ?? record.impact?.conflicting_bookings_count ?? 0,
    start_date: record.start_date || entry?.start_date || null,
    end_date: record.end_date || entry?.end_date || null,
    start_time: record.start_time ?? entry?.start_time ?? null,
    end_time: record.end_time ?? entry?.end_time ?? null
  }
}

/**
 * The blackouts that make this listing unsellable on this day.
 *
 * The availability feed names a blackout by id only (A6), so the records themselves come from the
 * settings list — the same source the booking calendar's day panel reads, and the same card, which
 * already renders scope, reason, listings and the edit/delete menu.
 */
const BlackoutSection = ({ reasons, date, onReview, onChanged }) => {
  const { t } = useTranslation()

  const {
    items,
    isSaving,
    isDeleting,
    formState,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete,
    refreshList,
    getMenuItems
  } = useBlackouts({ enabled: true })

  // A reason the settings list has not caught up with still deserves a card, built from the reason.
  const records = reasons.map((reason) => {
    const record = items.find((item) => isSameId(item.id, reason.blackout_id))
    if (record) return toDayCardShape(record, date)
    return toDayCardShape({ id: reason.blackout_id, scope: reason.scope, label: reason.label, reason: reason.detail }, date)
  })

  const submitAndRefresh = async (payload) => {
    await handleSubmit(payload)
    onChanged?.()
  }

  const deleteAndRefresh = async () => {
    await handleDelete()
    onChanged?.()
  }

  // The card's "requires action" count comes from the settings list, not the availability feed, so
  // a replacement has to refresh both or the card keeps offering Replace for a cleared conflict.
  const replacedAndRefresh = async () => {
    await refreshList()
    onChanged?.()
  }

  return (
    <Flex vertical gap="var(--spacing-s)">
      <span className={styles.sectionHeading}>
        {t("availability.blackouts_affecting", { defaultValue: "Blackouts affecting this experience" })}
      </span>

      {records.map((record, index) => (
        <BlackoutDayCard
          key={record.id || `${record.scope}-${index}`}
          blackout={record}
          date={date}
          menuItems={items.some((item) => isSameId(item.id, record.id)) ? getMenuItems(record) : []}
          onReview={() => onReview?.(record)}
          onReplaced={replacedAndRefresh}
        />
      ))}

      {formState && (
        <BlackoutFormModal
          isOpen
          scope={formState.scope}
          blackout={formState.blackout}
          onCancel={closeFormModal}
          onSubmit={submitAndRefresh}
          isSaving={isSaving}
        />
      )}

      <NewConfirmModal
        visible={!!blackoutToDelete}
        onClose={() => setBlackoutToDelete(null)}
        onConfirm={deleteAndRefresh}
        title={t("settings.blackout.delete_title", { defaultValue: "Delete Blackout?" })}
        message={<DeleteBlackoutMessage blackout={blackoutToDelete} date={date} />}
        warningText={t("settings.blackout.delete_warning", { defaultValue: "All related restrictions will be removed immediately." })}
        hasIcon={false}
        confirmLabel={t("settings.action_delete", { defaultValue: "Delete" })}
        loading={isDeleting}
      />
    </Flex>
  )
}

BlackoutSection.propTypes = {
  /** The day's blackout reasons — each carries the record id the card is built from. */
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      blackout_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      scope: PropTypes.string,
      label: PropTypes.string,
      detail: PropTypes.string
    })
  ).isRequired,
  date: PropTypes.string.isRequired,
  /** Opens the reservations a blackout strands; called with that record. */
  onReview: PropTypes.func,
  /** Called after a blackout is saved or deleted so the grid can refetch. */
  onChanged: PropTypes.func
}

export default BlackoutSection
